'use client';

import { useMemo, useState } from 'react';

import type { SelectedClient } from '../../tax-utils';

type TaxOverviewRowData = {
  readonly rowId: string;
  readonly clientId: string;
  readonly taxEnrollmentId: string | null;
  readonly salesforceId: string | null;
};

type UseTableRowSelectorReturn = {
  readonly selectedClients: SelectedClient[];
  readonly selectedCountOnPage: number;
  readonly allSelected: boolean;
  readonly someSelected: boolean;
  readonly toggleRow: (selectedClient: SelectedClient, checked: boolean) => void;
  readonly toggleAllOnPage: (rows: readonly TaxOverviewRowData[]) => void;
  readonly clear: () => void;
};

export function useTableRowSelector(currentPageRows: readonly TaxOverviewRowData[]): UseTableRowSelectorReturn {
  const [selectedClients, setSelectedClients] = useState<SelectedClient[]>([]);

  const selectedIds = useMemo(() => new Set(selectedClients.map((client) => client.id)), [selectedClients]);

  const selectedCountOnPage = useMemo(() => {
    if (currentPageRows.length === 0) return 0;
    return currentPageRows.filter((row) => selectedIds.has(row.rowId)).length;
  }, [currentPageRows, selectedIds]);

  const allSelected = useMemo(() => {
    if (currentPageRows.length === 0) return false;
    return selectedCountOnPage === currentPageRows.length;
  }, [currentPageRows, selectedCountOnPage]);

  const someSelected = useMemo(() => {
    if (currentPageRows.length === 0) return false;
    return selectedCountOnPage > 0 && selectedCountOnPage < currentPageRows.length;
  }, [currentPageRows, selectedCountOnPage]);

  const toggleRow = (selectedClient: SelectedClient, checked: boolean) => {
    setSelectedClients((prev) => {
      if (checked) {
        return [...prev, selectedClient];
      }
      return prev.filter((client) => client.id !== selectedClient.id);
    });
  };

  const toggleAllOnPage = (rows: readonly TaxOverviewRowData[]) => {
    setSelectedClients((prev) => {
      const currentPageIds = new Set(rows.map((row) => row.rowId));
      const prevSelectedIds = new Set(prev.map((client) => client.id));
      const selectedCountOnPage = rows.filter((row) => prevSelectedIds.has(row.rowId)).length;

      if (selectedCountOnPage > 0) {
        return prev.filter((client) => !currentPageIds.has(client.id));
      }

      const newSelections = rows.map(toSelectedClient);
      return [...prev, ...newSelections];
    });
  };

  const clear = () => {
    setSelectedClients([]);
  };

  return {
    selectedClients,
    selectedCountOnPage,
    allSelected,
    someSelected,
    toggleRow,
    toggleAllOnPage,
    clear,
  };
}

function toSelectedClient(row: TaxOverviewRowData): SelectedClient {
  return {
    id: row.rowId,
    clientId: row.clientId,
    taxEnrollmentId: row.taxEnrollmentId,
    salesforceId: row.salesforceId,
  };
}
